import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import styles from './AirportAutocomplete.module.css';

interface Airport {
  id: number;
  iata: string;
  name: string;
  city: string;
  state: string;
  country: string;
}

interface Props {
  label: string;
  value: Airport | null;
  onChange: (airport: Airport | null) => void;
}

const AirportAutocomplete: React.FC<Props> = ({ label, value, onChange }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Airport[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cache = useRef<Map<string, Airport[]>>(new Map());

  const search = useCallback(async (q: string) => {
    if (q.length < 2) { setResults([]); return; }
    const key = q.toLowerCase();
    if (cache.current.has(key)) { setResults(cache.current.get(key)!); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`read/airports/search?q=${encodeURIComponent(q)}&limit=8`);
      cache.current.set(key, data);
      setResults(data);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!open || !editing) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 300);
  }, [query, open, editing, search]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setEditing(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const displayValue = editing ? query : value ? `${value.iata} — ${value.city}` : query;

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.inputGroup}>
        <label className={styles.label}>{label}</label>
        <input
          className={styles.input}
          value={displayValue}
          autoComplete="off"
          onFocus={() => { setOpen(true); setEditing(true); }}
          onChange={e => { setQuery(e.target.value); setEditing(true); if (!e.target.value) onChange(null); }}
          onKeyDown={e => {
            if ((e.key === 'Backspace' || e.key === 'Delete') && !editing && value) {
              setEditing(true); setQuery(''); onChange(null); e.preventDefault();
            }
          }}
        />
        {loading && <span className={styles.spinner} />}
      </div>

      {open && (
        <div className={styles.dropdown}>
          {loading ? (
            <div className={styles.item}>Buscando...</div>
          ) : results.length > 0 ? results.map(a => (
            <div key={a.id} className={styles.item} onMouseDown={() => {
              onChange(a); setQuery(''); setEditing(false); setOpen(false); setResults([]);
            }}>
              <span className={styles.iata}>{a.iata}</span>
              <span className={styles.name}>{a.name}</span>
              <span className={styles.city}>{a.city}, {a.state}</span>
            </div>
          )) : query.length >= 2 ? (
            <div className={styles.item}>Nenhum aeroporto encontrado</div>
          ) : (
            <div className={styles.item}>Digite pelo menos 2 caracteres</div>
          )}
        </div>
      )}
    </div>
  );
};

export default AirportAutocomplete;
